// ============================================
// @engine/render2d - Public API
// ============================================

// Types
export * from './types';

// Core - Render Contexts
export {
  createRenderContext,
  RenderContext,
  WebGPURenderContext,
  type IRenderContext,
  type RenderContextOptions,
  isWebGPUAvailable,
} from './core/RenderContext';
export { CommandBuffer, DrawList, createDrawEntry, type DrawEntry, type SortMode } from './core/CommandBuffer';
export { RenderPass, OffscreenTarget, type RenderPassOptions } from './core/RenderPass';

// Compute
export {
  ComputePass,
  ComputeBuffer,
  ComputeTexture,
  ComputePipelineBuilder,
  type ComputePassOptions,
  type ComputeBufferOptions,
  type ComputeTextureOptions,
} from './core/ComputePass';

// User-space compute shaders (.slang kernel assets — scan/wrapper/reflection/cache)
export {
  scanKernels,
  type ScannedKernel,
  type KernelScanResult,
} from './compute/kernelScan';
export {
  prepareComputeModule,
  remapDiagnostic,
  entryPointOf,
  instanceNameOf,
  KERNEL_ENTRY_PREFIX,
  KERNEL_INSTANCE_PREFIX,
  type PreparedComputeModule,
} from './compute/wrapperGen';
export { reflectCompute } from './compute/reflectCompute';
export {
  ScriptComputeRuntime,
  type ComputeModuleArtifact,
  type ComputeKernelRuntimeHandle,
} from './compute/ScriptComputeRuntime';
export { computeStructLayout, typeLayoutOf, typeLabelOf, type LayoutSpace } from './compute/wgslLayout';
export {
  COMPUTE_SHADER_CACHE_DIR,
  computeShaderId,
  computeShaderPath,
  registerComputeShader,
  registerComputeShaders,
  hasComputeShader,
  getComputeShaderOrNull,
  clearComputeShaders,
} from './compute/ComputeShaderCache';
export type {
  ComputeShaderReflection,
  ComputeKernelReflection,
  ComputeBindingEntry,
  ComputeStructLayout,
  ComputeFieldLayout,
  ComputeResourceKind,
  ComputeDiag,
} from './compute/types';

// Pipeline - Materials
export { Material, Shader, type MaterialOptions } from './pipeline/Material';
export { ShaderProgram, computeOverrideKey } from './pipeline/ShaderProgram';
export { PropertyBlock } from './pipeline/PropertyBlock';

// Pipeline - Material assets (serialized .mat + WGSL @material marker reflection)
export {
  reflectWgsl,
  isMaterialShader,
  propertyTypeToGPUType,
  buildUniformDef,
  buildSpriteUniformDef,
  materialDefaults,
  type MaterialReflection,
  type MaterialProperty,
  type MaterialOverride,
  type MaterialTextureSlotDef,
  type MaterialPropertyType,
  type MaterialOverrideType,
  type MaterialRendererKind,
} from './pipeline/MaterialReflection';
export { reflectSlang, type SlangReflectionJson } from './pipeline/reflectSlang';
export { toHalf, fromHalf } from './core/half';
export {
  registerCompiledShader,
  registerCompiledShaders,
  hasCompiledShader,
  getCompiledShader,
  compiledShaderNames,
  getAllCompiledShaders,
  clearCompiledShaders,
} from './pipeline/ShaderCache';
export {
  loadMaterialAsset,
  parseMaterialAsset,
  serializeMaterialAsset,
  createDefaultMaterialData,
  validateMaterialData,
  MATERIAL_ASSET_VERSION,
  type MaterialAssetData,
  type MaterialLoadResolvers,
} from './pipeline/MaterialAsset';
export {
  MaterialCache,
  type MaterialCacheDeps,
  type ResolvedMaterial,
} from './pipeline/MaterialCache';
export {
  MaterialRegistry,
  materialRegistry,
  BUILTIN_SPRITE_DEFAULT,
  BUILTIN_SPRITE_LIT,
  BUILTIN_TEXT_MSDF,
  BUILTIN_VFX_DEFAULT,
  type MaterialFactory,
} from './pipeline/MaterialRegistry';

// Pipeline - GBuffer Target (MRT convenience wrapper)
export {
  GBufferTarget,
  GBuffer,
  GBUFFER_FORMATS,
} from './pipeline/GBufferTarget';

// Pipeline - Deferred Lighting
export {
  DeferredLightingPipeline,
} from './pipeline/DeferredLightingPipeline';

// Lighting
export {
  type Light2D,
  type Light2DBase,
  type PointLight2D,
  type SpotLight2D,
  type DirectionalLight2D,
  type AmbientLight2D,
  type EmissionMode,
  type IESProperties,
  type VolumetricProperties,
  createPointLight,
  createSpotLight,
  createDirectionalLight,
  createAmbientLight,
  kelvinToRGB,
  applyTint,
  resolveEmissionColor,
  KELVIN_PRESETS,
  GPU_LIGHT_STRUCT,
  type GPULightFields,
  LIGHT_UNIFORMS,
  type LightUniformFields,
  GPULightType,
  MAX_LIGHTS,
  GPU_LIGHT_SIZE,
  LIGHT_BUFFER_SIZE,
  getMaxLights,
  getMaxShadowLights,
  getShadowMapResolution,
  getMaxShadowEdges,
  graphicsConfig,
  LightManager,
  LightCullPass,
  TILE_SIZE,
  MAX_LIGHTS_PER_TILE,
  TILE_STRIDE,
  VFXLightInjectPass,
  type LightEmittingSystem,
  // Base classes
  LightingComputePass,
  // IES Profiles
  IESProfileManager,
  type IESProfile,
  type IESPreset,
  IES_PRESETS,
  IES_PROFILE_RESOLUTION,
  MAX_IES_PROFILES,
  generateIESPreset,
  createIESProfile,
  // Shadows
  ShadowMapPass,
  type ShadowCaster2D,
  type ShadowCasterShape,
  type ShadowBoxShape,
  type ShadowCircleShape,
  type ShadowPolygonShape,
  type ShadowEdgesShape,
  extractEdges,
  createShadowBox,
  createShadowCircle,
  createShadowPolygon,
  MAX_SHADOW_LIGHTS,
  SHADOW_MAP_RESOLUTION,
  MAX_SHADOW_EDGES,
  // Volumetric Lighting
  VolumetricLightingPass,
  type VolumetricLightingConfig,
  // GraphicsConfig
  type GraphicsConfig,
  type RenderPath,
  DEFAULT_GRAPHICS_CONFIG,
  setGraphicsConfig,
  getTileOverrides,
  getShadowSamplingOverrides,
  getShadowComputeOverrides,
  getLightAccumOverrides,
  getForwardPlusOverrides,
} from './lighting';

// Pipeline - Custom Passes
export {
  CustomPass,
  InjectionPoint,
  type CustomPassContext,
} from './pipeline/CustomPass';

export {
  FullscreenPass,
} from './pipeline/FullscreenPass';

// Pipeline - Deferred Passes
export {
  DeferredPass,
  DeferredPassManager,
  DeferredInjectionPoint,
  type DeferredPassResources,
} from './pipeline/DeferredPass';

// Pipeline - Post Processing
export {
  PostProcessStack,
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
  type PostProcessEffectDesc,
} from './pipeline/PostProcessStack';

// Struct Layout utilities for WebGPU uniform buffers
export {
  GPUType,
  type GPUTypeInfo,
  type GPUVertexFormat,
  ScalarTypeBuilder,
  type StructDef,
  type StructFieldDef,
  StructWriter,
  createStructWriter,
  defineStruct,
  printStructLayout
} from "./core/GPUTypes";

// Pre-built post-process uniform layouts
export * from './pipeline/PostProcessLayouts';

// Post-Processing Effects
export {
  BloomEffect,
  ChromaticAberrationEffect,
  ColorGradingEffect,
  LensDistortionEffect,
  VignetteEffect,
  ChannelMixerEffect,
  FilmGrainEffect,
  LiftGammaGainEffect,
  MotionBlurEffect,
  WhiteBalanceEffect,
  TonemapEffect,
  TonemapMode,
  GodRaysEffect,
  KuwaharaEffect,
  CRTEffect,
  HeatHazeEffect,
  GlitchEffect,
  HalftoneEffect,
  PixelArtEffect,
  PixelArtPalette,
  UnderwaterEffect,
  OutlineEffect,
  ZoomBlurEffect,
  builtinEffects,
  createEffect,
  type EffectRegistryEntry,
} from './pipeline/effects';

// Batch
export { SpriteBatch } from './batch/SpriteBatch';
export { ShapeBatch } from './batch/ShapeBatch';

// Objects
export { Camera2D } from './objects/Camera2D';
export { Sprite, Quad } from './objects/Sprite';
export { TextureManager, type TextureRegion, type SpriteFrame, type Animation, type BuiltinTexture } from './objects/TextureManager';

// Re-export commonly used types
export type {
  GPUBackend,
  TextureHandle,
  BufferHandle,
  PipelineHandle,
  ShaderHandle,
  RenderTargetHandle,
  MRTRenderTargetHandle,
  ComputePipelineHandle,
  TextureDescriptor,
  BufferDescriptor,
  PipelineDescriptor,
  ComputePipelineDescriptor,
  ComputeBindGroup,
  DispatchSize,
  StorageBufferBinding,
  StorageTextureBinding,
  ShaderParameter,
  ShaderMetadata,
  ShaderParamType,
  RenderCommand,
  RenderStats,
  BlendMode,
  MRTAttachmentDescriptor,
  MRTRenderTargetDescriptor,
  MRTRenderTargetInfo,
  TextureSlotName,
  TextureSlotIndex,
  MaterialTag,
  RenderState,
  StencilFaceState,
  StencilOperation,
} from './types';
